import i18next from 'i18next';
import { en } from './locales/en.js';
import { ru } from './locales/ru.js';

export type LandingLocale = 'en' | 'ru';

export const AVAILABLE_LOCALES: readonly LandingLocale[] = ['en', 'ru'];

const STORAGE_KEY = 'bn-games-landing-locale';

function syncDocumentLocale(): void {
  document.title = i18next.t('seo.title');
  document.documentElement.lang = i18next.language;
}

export function getStoredLocale(): LandingLocale {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (AVAILABLE_LOCALES.includes(raw as LandingLocale)) return raw as LandingLocale;
  } catch {
    // localStorage may be unavailable (private mode, etc.) — fall through.
  }
  return 'en';
}

export function storeLocale(lang: LandingLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Persistence is best-effort.
  }
}

async function initInstance(lang: LandingLocale): Promise<void> {
  await i18next.init({
    lng: lang,
    fallbackLng: 'en',
    resources: {
      en: { translation: en },
      ru: { translation: ru },
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });
}

export async function initI18n(lang: LandingLocale): Promise<void> {
  await initInstance(lang);
  syncDocumentLocale();
}

/**
 * For pages whose own copy is not translated (/friends, /profile, /signin) but
 * which still host a translated overlay. It leaves `document.title` and
 * `<html lang>` alone, since those belong to the page, and it does nothing
 * when `initI18n` already ran.
 */
export async function ensureI18n(lang: LandingLocale): Promise<void> {
  if (i18next.isInitialized) return;
  await initInstance(lang);
}

export async function setLocale(lang: LandingLocale): Promise<void> {
  await i18next.changeLanguage(lang);
  storeLocale(lang);
  syncDocumentLocale();
}

export function currentLocale(): LandingLocale {
  return i18next.language === 'ru' ? 'ru' : 'en';
}

export const t = i18next.t.bind(i18next);
